import type { Bridgedata } from '@/apiFzu/ApiFzu'
import type { MultiLineString } from 'geojson'
import * as Cesium from 'cesium'

// 添加绘制桥梁名称标签的函数
export function createBridgeLabels(data: Bridgedata[]): Cesium.Entity[] {
  const labels: Cesium.Entity[] = []

  data.forEach((bridge) => {
    // 收集桥面所有坐标点
    const allCoordinates: number[][] = []

    // 从桥面获取坐标
    bridge.deck.forEach((deck) => {
      const geom = deck.geom as unknown as MultiLineString
      geom.coordinates.forEach((line) => {
        line.forEach((point) => {
          allCoordinates.push(point)
        })
      })
    })

    if (allCoordinates.length === 0) return

    // 找出最远的两个点来确定桥梁方向和轴线
    let maxDistance = 0
    let startPoint: number[] = [0, 0]
    let endPoint: number[] = [0, 0]

    for (let i = 0; i < allCoordinates.length; i++) {
      for (let j = i + 1; j < allCoordinates.length; j++) {
        const p1 = allCoordinates[i]
        const p2 = allCoordinates[j]
        const distance = Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2))

        if (distance > maxDistance) {
          maxDistance = distance
          startPoint = p1
          endPoint = p2
        }
      }
    }

    // 计算桥梁中点作为标签位置
    const centerLon = (startPoint[0] + endPoint[0]) / 2
    const centerLat = (startPoint[1] + endPoint[1]) / 2

    // 计算桥梁方向角（弧度）
    const deltaX = endPoint[0] - startPoint[0]
    const deltaY = endPoint[1] - startPoint[1]
    let angle = Math.atan2(deltaY, deltaX) // 方向角，单位为弧度
    if (angle < 0) {
      //angle = Math.PI / 24 - angle;
      angle = Math.PI + angle
    }

    // 创建带有label的实体（label平行于桥梁方向）
    const entity = new Cesium.Entity({
      position: Cesium.Cartesian3.fromDegrees(centerLon, centerLat), // 稍微抬高一点
      label: {
        // label属性设置
        text: bridge.bridge.split('').join('\n'), // 将桥梁名称拆成单个字并换行
        font: 'bold 28px san-size', // 字体样式
        fillColor: Cesium.Color.YELLOW, // 字体颜色
        outlineColor: Cesium.Color.BLACK, // 描边颜色
        outlineWidth: 2, // 描边宽度
        style: Cesium.LabelStyle.FILL_AND_OUTLINE, // 填充和描边样式
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        // 确保始终显示
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        // 设置距离缩放
        scaleByDistance: new Cesium.NearFarScalar(500, 1.0, 10000, 0.5),
        translucencyByDistance: new Cesium.NearFarScalar(1000, 1.0, 8000, 0.6),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(200.0, 5000.0),
      },
      properties: bridge,
    })

    labels.push(entity)
  })

  return labels
}

export function drawDeck(data: Bridgedata[]) {
  const appearance = new Cesium.PolylineMaterialAppearance({
    material: Cesium.Material.fromType('Color', {
      color: Cesium.Color.BLACK, // 设置为黑色
    }),
    translucent: false,
  })

  return data.map((el) => {
    const deckPrimitives = el.deck.map((d) => {
      const geom = d.geom as unknown as MultiLineString
      const positions = geom.coordinates.map((coord) => {
        return Cesium.Cartesian3.fromDegreesArray(coord.flat(1))
      })
      const instances = positions.map(
        (position) =>
          new Cesium.GeometryInstance({
            geometry: new Cesium.GroundPolylineGeometry({
              positions: position,
              width: 2.0,
            }),
            attributes: {
              color: new Cesium.ColorGeometryInstanceAttribute(1.0, 0.0, 0.0, 1.0), // 黑色不透明
            },
          })
      )
      // 创建单一 Primitive
      const primitive = new Cesium.GroundPolylinePrimitive({
        geometryInstances: instances,
        appearance: appearance,
        // 优化渲染批次
        interleave: true,
      })

      // 添加内存优化逻辑
      const cleanup = () => {
        primitive.appearance.material.destroy()
      }
      primitive.destroy = () => {
        cleanup()
        Cesium.GroundPolylinePrimitive.prototype.destroy.call(primitive)
      }
      return primitive
    })
    return { id: el.id, deckPrimitives }
  })
}

export function drawPier(data: Bridgedata[]) {
  const appearance = new Cesium.PolylineMaterialAppearance({
    material: Cesium.Material.fromType('Color', {
      color: Cesium.Color.BLACK, // 设置为黑色
    }),
    translucent: false,
  })

  return data.map((bridge) => {
    const pierPrimitives = bridge.pier.map((pier) => {
      const geom = pier.geom as unknown as MultiLineString
      const positions = geom.coordinates.map((coord) => {
        return Cesium.Cartesian3.fromDegreesArray(coord.flat(1))
      })

      const instances = positions.map(
        (position) =>
          new Cesium.GeometryInstance({
            geometry: new Cesium.GroundPolylineGeometry({
              positions: position,
              width: 2.0,
            }),
            attributes: {
              color: new Cesium.ColorGeometryInstanceAttribute(1.0, 0.0, 0.0, 1.0), // 黑色不透明
            },
          })
      )
      const primitive = new Cesium.GroundPolylinePrimitive({
        geometryInstances: instances,
        appearance: appearance,
        // 优化渲染批次
        interleave: true,
      })

      // 添加内存优化逻辑
      const cleanup = () => {
        primitive.appearance.material.destroy()
      }
      primitive.destroy = () => {
        cleanup()
        Cesium.GroundPolylinePrimitive.prototype.destroy.call(primitive)
      }
      return primitive
    })
    return { id: bridge.id, pierPrimitives }
  })
}
